'use client';

import { logger } from '@adornis/base/logging.js';
import { ArrowLeft, BookOpen, CheckCircle2, ChevronRight, Clock, GraduationCap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExamsByClassQuery } from '../../../db/api/get-exams-by-class.js';
import { Exam } from '../../../db/exam.js';
import { isSubject } from '../../../db/subjects.js';
import { Button } from '../ui/button.js';

interface ClassInsightsPageProps {
  className: string;
  onBack: () => void;
  onOpenExam: (conversationId: string) => void;
}

/** A completed exam is one whose grading has fully finished — every other status still needs work. */
const isGraded = (status: string | null | undefined) => status === 'graded';

export function ClassInsightsPage({ className, onBack, onOpenExam }: ClassInsightsPageProps) {
  const { t, i18n } = useTranslation();

  const [exams, setExams] = useState<Exam[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!className) return;

    let isMounted = true;
    setIsLoading(true);

    void getExamsByClassQuery(className)({
      _id: 1,
      conversationId: 1,
      title: 1,
      subject: 1,
      topic: 1,
      status: 1,
      createdAt: 1,
    })
      .then(result => {
        if (!isMounted) return;
        setExams(result ?? []);
      })
      .catch(err => {
        logger.error(err, 'Failed to load exams for class');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [className]);

  const subjectCount = useMemo(() => new Set(exams.map(e => e.subject).filter(Boolean)).size, [exams]);
  const gradedCount = useMemo(() => exams.filter(e => isGraded(e.status)).length, [exams]);

  const subjectLabel = (subject?: string | null) =>
    subject && isSubject(subject) ? t(`Subjects.${subject}`) : subject || t('ClassInsights.unknownSubject');

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  };

  const statTiles = [
    { key: 'examCount', Icon: BookOpen, value: exams.length },
    { key: 'subjectCount', Icon: GraduationCap, value: subjectCount },
    { key: 'gradedCount', Icon: CheckCircle2, value: gradedCount },
  ] as const;

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center justify-start gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl sm:text-xl font-bold text-foreground tracking-tight">{className}</h1>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6">{t('ClassInsights.overview')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {statTiles.map(({ key, Icon, value }) => (
                <div key={key} className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg">
                  <div className="w-10 h-10 bg-brand-surface rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-brand-text" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-foreground">{isLoading ? '...' : value}</p>
                    <p className="text-sm text-muted-foreground">{t(`ClassInsights.${key}`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6">{t('ClassInsights.examListTitle')}</h2>

            {isLoading ? (
              <p className="text-sm text-muted-foreground">{t('ClassInsights.loading')}</p>
            ) : exams.length === 0 ? (
              <p className="text-sm italic text-muted-foreground/70">{t('ClassInsights.noExams')}</p>
            ) : (
              <div className="space-y-2">
                {exams.map(exam => (
                  <button
                    key={exam._id}
                    type="button"
                    onClick={() => exam.conversationId && onOpenExam(exam.conversationId)}
                    className="w-full flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg text-left hover:bg-accent transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {exam.title || t('ClassInsights.unnamedExam')}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                          {subjectLabel(exam.subject)}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            isGraded(exam.status)
                              ? 'bg-green-50 text-green-700'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {isGraded(exam.status) ? t('ClassInsights.statusGraded') : t('ClassInsights.statusInProgress')}
                        </span>
                        {exam.createdAt && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" /> {formatDate(exam.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
